import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import type { CSSProperties } from "react";
import type { RoundName } from "../../domain/round/types";
import { vehicleVisualIdentity } from "../../domain/transport/identity";
import type { TransportDirection, VehicleTrip } from "../../domain/transport/types";
import { ApiError, api, type TournamentSummary } from "../../lib/api";
import { initialRoundName, initialSeason, initialTournamentId, persistRoundSelection } from "../../lib/roundSelection";
import "../rounds/round-workspace.css";
import "./transport-overview.css";

const ROUNDS: RoundName[] = ["R1", "R2", "R3", "R4"];

function chooseTournament(rows: TournamentSummary[]) {
  if (!rows.length) return "";
  const today = new Date().toISOString().slice(0, 10);
  const active = rows.find((row) => row.startDate <= today && (!row.endDate || row.endDate >= today));
  if (active) return active.id;
  const started = rows.filter((row) => row.startDate && row.startDate <= today);
  return (started.at(-1) || rows[0]).id;
}

function clock(minutes: number) {
  const value = Math.max(0, Math.round(minutes || 0));
  return `${String(Math.floor(value / 60)).padStart(2, "0")}:${String(value % 60).padStart(2, "0")}`;
}

function DirectionTable({ direction, trips, people, vehicles, bases }: {
  direction: TransportDirection;
  trips: VehicleTrip[];
  people: Map<string, string>;
  vehicles: Map<string, { name?: string; capacity?: number; baseName?: string }>;
  bases: Map<string, string>;
}) {
  const title = direction === "to-course" ? "Dojazd na pole" : "Powrót z pola";
  const eyebrow = direction === "to-course" ? "TO COURSE" : "RETURN";
  return (
    <section className={`transport-overview-direction ${direction}`}>
      <header><div><span className="eyebrow">{eyebrow}</span><h3>{title}</h3></div><strong>{trips.length} kursów</strong></header>
      <div className="transport-overview-cars">
        {trips.map((trip) => {
          const visual = vehicleVisualIdentity(trip.vehicleId);
          const style = { "--car-bg": visual.background, "--car-border": visual.border, "--car-fg": visual.foreground } as CSSProperties;
          const vehicle = vehicles.get(trip.vehicleId);
          const members = [trip.driverPersonId, ...trip.passengerPersonIds].filter(Boolean);
          const isXL = trip.vehicleId.startsWith("uber-") && members.length > 3;
          return (
            <article className="transport-overview-car" style={style} key={trip.id}>
              <div className="transport-overview-car-head">
                <div><strong>{vehicle?.name || trip.vehicleId}{isXL ? " · XL" : ""}</strong><small>{bases.get(trip.baseId) || vehicle?.baseName || "—"}</small></div>
                <span>{members.length}{vehicle?.capacity ? ` / ${vehicle.capacity}` : ""} os.</span>
              </div>
              <div className="transport-overview-meta">
                <span><small>{direction === "to-course" ? "WYJAZD" : "ODJAZD"}</small><strong>{clock(trip.departAtMinutes)}</strong></span>
                <span><small>PRZYJAZD</small><strong>{clock(trip.arriveAtMinutes)}</strong></span>
                <span><small>SPREAD</small><strong>{trip.waitSpreadMinutes} min</strong></span>
                <span><small>KURS</small><strong>{trip.shuttle ? `SHUTTLE ${trip.tripNumber}` : trip.tripNumber}</strong></span>
              </div>
              <table><thead><tr><th>#</th><th>Osoba</th><th>Rola</th></tr></thead><tbody>{members.map((personId, index) => {
                const driver = personId === trip.driverPersonId;
                return <tr key={`${trip.id}-${personId}`} className={driver ? "driver" : "passenger"}><td>{index + 1}</td><td>{people.get(personId) || personId}</td><td>{driver ? (trip.vehicleId.startsWith("uber-") ? "ZAMAWIA" : "KIEROWCA") : "PASAŻER"}</td></tr>;
              })}</tbody></table>
            </article>
          );
        })}
        {!trips.length ? <div className="transport-overview-empty">Brak kursów w tym kierunku.</div> : null}
      </div>
    </section>
  );
}

export function RoundTransportPage() {
  const queryClient = useQueryClient();
  const [season, setSeason] = useState(initialSeason);
  const [tournamentId, setTournamentId] = useState(initialTournamentId);
  const [round, setRound] = useState<RoundName>(initialRoundName);
  const [notice, setNotice] = useState("");
  const autoRoundKeyRef = useRef("");

  const tournaments = useQuery({ queryKey: ["tournaments", season], queryFn: () => api.tournaments(season) });
  useEffect(() => {
    const rows = tournaments.data?.tournaments || [];
    if (rows.length && (!tournamentId || !rows.some((row) => row.id === tournamentId))) setTournamentId(chooseTournament(rows));
  }, [tournaments.data, tournamentId]);

  const roundWorkspaces = useQueries({
    queries: ROUNDS.map((roundName) => ({
      queryKey: ["round-workspace", tournamentId, roundName],
      queryFn: () => api.roundWorkspace(tournamentId, roundName),
      enabled: Boolean(tournamentId),
      staleTime: 10_000,
    })),
  });
  const roundStateSignature = roundWorkspaces.map((state) => state.isPending ? "pending" : state.isError ? "error" : state.data?.confirmation.confirmed ? "confirmed" : "open").join("|");

  useEffect(() => {
    if (!tournamentId) return;
    const key = `${season}:${tournamentId}`;
    if (autoRoundKeyRef.current === key) return;
    if (roundWorkspaces.some((state) => state.isPending || state.isError || !state.data)) return;
    autoRoundKeyRef.current = key;
    const nextIndex = roundWorkspaces.findIndex((state) => !state.data?.confirmation.confirmed);
    const nextRound: RoundName = nextIndex >= 0 ? ROUNDS[nextIndex] : "R4";
    if (round !== nextRound) setRound(nextRound);
  }, [tournamentId, season, round, roundStateSignature]);

  useEffect(() => {
    if (tournamentId) persistRoundSelection(tournamentId, round, season);
    setNotice("");
  }, [tournamentId, round, season]);

  const [maxMorningWait, setMaxMorningWait] = useState(150);
  const [maxReturnWait, setMaxReturnWait] = useState(120);
  const workspace = useQuery({ queryKey: ["round-workspace", tournamentId, round], queryFn: () => api.roundWorkspace(tournamentId, round), enabled: Boolean(tournamentId) });
  const data = workspace.data;
  const selectedTournament = tournaments.data?.tournaments.find((row) => row.id === tournamentId);
  const people = useMemo(() => new Map((data?.staff || []).map((row) => [row.personId, row.displayName])), [data?.staff]);
  const vehicles = useMemo(() => new Map((data?.resources.vehicles || []).map((row) => [row.id, row])), [data?.resources.vehicles]);
  const bases = useMemo(() => new Map((data?.resources.bases || []).map((row) => [row.id, row.name])), [data?.resources.bases]);

  const recalculate = useMutation({
    mutationFn: async () => {
      const current = workspace.data;
      if (!current?.schedule) throw new Error("Brak rozpiski do przeliczenia transportu.");
      if (current.confirmation.confirmed && !current.availability.working) throw new Error("Ta runda jest zatwierdzona. Otwórz ją ponownie przed zmianą transportu.");
      const preview = await api.transportPreview(tournamentId, round, {
        expectedRevision: current.workspaceRevision,
        schedule: current.schedule,
        currentTransport: current.transport,
        maxMorningWait,
        maxReturnWait,
      });
      const blockers = preview.plan.issues.filter((issue) => issue.severity === "blocker");
      if (blockers.length) throw new Error(`Nie zapisano transportu: ${blockers.length} blocker${blockers.length === 1 ? "" : "ów"}. Otwórz Edycję transportu, żeby je poprawić.`);
      return api.saveRoundWorkspace(tournamentId, round, {
        expectedRevision: current.workspaceRevision,
        schedule: current.schedule,
        transport: preview.plan,
        transportStale: false,
      });
    },
    onSuccess: (result) => {
      queryClient.setQueryData(["round-workspace", tournamentId, round], result);
      void queryClient.invalidateQueries({ queryKey: ["round-workspace", tournamentId, round] });
      setNotice(`Transport przeliczony i zapisany. Workspace r${result.workspaceRevision}.`);
    },
  });

  const error = tournaments.error || workspace.error;
  const errorText = error instanceof ApiError ? error.message : error instanceof Error ? error.message : "";
  const recalcError = recalculate.error instanceof ApiError ? recalculate.error.message : recalculate.error instanceof Error ? recalculate.error.message : "";
  const search = `?tournament=${encodeURIComponent(tournamentId)}&round=${round}&season=${season}`;
  const confirmedReadOnly = Boolean(data?.confirmation.confirmed && !data?.availability.working);

  return (
    <section className="page-grid transport-overview-page">
      <article className="panel round-context-panel">
        <div className="round-context-copy"><span className="eyebrow">RUNDA · TRANSPORT</span><h2>{selectedTournament?.name || "Wybierz turniej"}</h2><p>Dojazd i powrót są pokazane osobno, ale kolory fizycznych aut są stałe w całej aplikacji i eksporcie PNG.</p></div>
        <div className="round-context-controls">
          <label><span>Sezon</span><select value={season} onChange={(event) => setSeason(Number(event.target.value))}>{[2026, 2025, 2024].map((value) => <option key={value}>{value}</option>)}</select></label>
          <label className="tournament-select"><span>Turniej</span><select value={tournamentId} onChange={(event) => setTournamentId(event.target.value)}>{(tournaments.data?.tournaments || []).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
          <div className="round-switch">{ROUNDS.map((value, index) => <button key={value} type="button" className={round === value ? "active" : ""} onClick={() => setRound(value)}>{value}{roundWorkspaces[index]?.data?.confirmation.confirmed ? " ✓" : ""}</button>)}</div>
        </div>
      </article>
      {errorText || recalcError ? <div className="transport-overview-error">{errorText || recalcError}</div> : null}
      {notice ? <div className="transport-overview-notice">{notice}</div> : null}
      {data?.transport ? (
        <>
          <div className={`transport-overview-toolbar ${data.transportStale ? "stale" : "current"}`}>
            <div><span>workspace r{data.workspaceRevision}</span><strong>{data.transportStale ? "TRANSPORT STALE · wymaga przeliczenia" : "PLAN AKTUALNY"}</strong></div>
            <div>
              <label className="transport-overview-wait-limit"><span>Limit rano (min)</span><input type="number" min={0} max={360} value={maxMorningWait} onChange={(event) => setMaxMorningWait(Math.max(0, Number(event.target.value) || 0))} /></label>
              <label className="transport-overview-wait-limit"><span>Limit powrót (min)</span><input type="number" min={0} max={360} value={maxReturnWait} onChange={(event) => setMaxReturnWait(Math.max(0, Number(event.target.value) || 0))} /></label>
              <button className={data.transportStale ? "primary-button" : "secondary-button"} type="button" disabled={!data.schedule || confirmedReadOnly || recalculate.isPending} onClick={() => recalculate.mutate()}>{recalculate.isPending ? "Przeliczam…" : data.transportStale ? "Przelicz i zapisz transport" : "Przelicz ponownie"}</button>
              <Link className="secondary-button" to={`/round/transport/edit${search}`}>Edytuj ręcznie</Link>
              <Link className="secondary-button" to={`/round/export${search}`}>Pobierz PNG</Link>
            </div>
          </div>
          <div className="round-kpi-grid transport-kpi-grid">
            <article className="panel round-kpi"><span>Dojazd max</span><strong>{data.transport.metrics.morningMaxWaitMinutes} min</strong><small>{data.transport.departures.length} kursów</small></article>
            <article className="panel round-kpi"><span>Powrót max</span><strong>{data.transport.metrics.returnMaxWaitMinutes} min</strong><small>{data.transport.returns.length} kursów</small></article>
            <article className="panel round-kpi"><span>Kluczyki</span><strong>{data.transport.policy.keyPolicy}</strong><small>fizyczna flota</small></article>
          </div>
          <DirectionTable direction="to-course" trips={data.transport.departures} people={people} vehicles={vehicles} bases={bases} />
          <DirectionTable direction="return" trips={data.transport.returns} people={people} vehicles={vehicles} bases={bases} />
        </>
      ) : workspace.isPending ? <article className="panel round-loading"><div className="round-loading-bar"/><span>Wczytuję transport…</span></article> : <article className="panel round-empty">Brak transportu. Wygeneruj rundę albo przelicz plan w Edycji.</article>}
    </section>
  );
}
